import { AmbientStage } from '@/components/AmbientStage';
import { BottomTabs } from '@/components/BottomTabs';
import { CommandPaletteProvider } from '@/components/CommandPalette';
import { NavRail } from '@/components/NavRail';
import { PageTransition } from '@/components/PageTransition';
import { RatingPromptProvider } from '@/components/RatingPrompt';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useT } from '@/i18n/locale';
import { ambientTarget } from '@/lib/ambient';

/**
 * The persistent frame around every auth-gated route (ui-ux.md §3):
 * left rail (md+), bottom tabs (< md), and a centred content column capped at
 * 1200px with responsive gutters. The rail's width is one CSS variable
 * (`--rail-w`, index.css `.app-shell`): 64px at md, 232px from lg, 64px when
 * the viewer collapses it — `<main>`'s padding and the xl title stage read
 * the same value, so they track the rail as it animates. Route content animates inside
 * <PageTransition/> — the shell itself never remounts on navigation.
 *
 * `<CommandPaletteProvider>` owns the ⌘K palette: the global ⌘K / Ctrl+K /
 * `/` shortcuts work on every in-shell route, and the rail's search row opens
 * the same instance.
 *
 * Wrapped in one shared `<TooltipProvider>` (the idiomatic single-provider
 * Radix pattern) because the collapsed tablet rail's icon-only nav items
 * render `<Tooltip>` — without a provider ancestor, Radix throws
 * "'Tooltip' must be used within 'TooltipProvider'" and blanks the whole
 * shell at md-lg widths.
 */
export function AppShell() {
  const t = useT();
  return (
    <TooltipProvider>
      <CommandPaletteProvider>
      <RatingPromptProvider>
      {/* `isolate`: the ambient wash (z -1) paints over this canvas, under all content. */}
      <div ref={ambientTarget} className="app-shell isolate min-h-dvh bg-bg">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2 focus:text-body focus:text-ink focus:shadow-sm"
        >
          {t('common.skipToContent')}
        </a>
        <AmbientStage />
        <NavRail />
        {/* overflow-x-clip: a full-bleed stage (index.css .stage-bleed) is
            sized from 100vw, which overshoots by a classic scrollbar's width.
            `clip` is not a scroll container, so sticky children still pin. */}
        <main id="main-content" className="app-main xl:overflow-x-clip">
          <div className="mx-auto w-full max-w-[1200px] px-4 pb-28 pt-8 sm:px-6 md:pb-16 md:pt-10 lg:px-8">
            {/* Feature screens are route-split (App.tsx); the Suspense boundary
                for lazy chunks lives inside PageTransition, below AnimatePresence. */}
            <PageTransition />
          </div>
        </main>
        <BottomTabs />
      </div>
      </RatingPromptProvider>
      </CommandPaletteProvider>
    </TooltipProvider>
  );
}
